import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  UseFormProps,
  Fields,
  Submit,
  ObjectValue,
  HandleChangeFn,
  InputProps,
} from 'features/form/types';
import { ValidationError } from 'yup';
import { produce, original } from 'immer';
import { trim } from 'utils';

export const formatValue = (
  resetValue: boolean,
  stateValue: string | boolean | number | undefined | Record<string, unknown>,
  initialValue: unknown
) => {
  if (resetValue) {
    return '';
  }
  if (typeof initialValue === 'string' || typeof initialValue === 'number') {
    return String(initialValue);
  } else if (typeof initialValue === 'boolean') {
    return Boolean(initialValue);
  } else if (typeof initialValue === 'object') {
    return { ...(initialValue ?? {}) };
  }
  return stateValue;
};

export const formatFormState = (
  state: Fields,
  fields: Fields,
  handleChange: HandleChangeFn,
  subIndex?: number,
  subKey?: string,
  resetValue?: boolean
): Fields => {
  const newState = { ...state };
  Object.keys(state).forEach(key => {
    if (state?.[key] != null) {
      newState[key] = {
        ...newState[key],
        value: resetValue ? '' : state[key].value,
        onChange: handleChange(key, subIndex, subKey),
        subFields: state[key].subFields
          ? state[key].subFields?.map((item: Fields, index) =>
              formatFormState(
                item,
                fields[key]?.subFields?.[index] || item,
                handleChange,
                index,
                key
              )
            )
          : undefined,
        error: undefined,
        dirty: resetValue ? false : newState[key].dirty,
        touched: resetValue ? false : newState[key].touched,
      };
    }
  });
  return newState;
};

export const formatSubmit = (state: Fields): Submit => {
  const submitState: Submit = {};
  Object.keys(state).forEach(key => {
    if (state?.[key] != null) {
      if (state[key].subFields) {
        submitState[key] = state[key].subFields?.map((item: Fields) => formatSubmit(item));
      } else if (state[key].type === 'value') {
        submitState[key] =
          state[key].value !== undefined || state[key].value === ''
            ? state[key].value
            : state[key].originalValue;
      } else if (state[key].objectValue) {
        submitState[key] = state[key].objectValue;
      } else {
        submitState[key] = state[key].value || '';
      }
    }
  });
  return submitState;
};

export const addArrayToState = (
  prevState: Fields,
  field: string,
  array: Fields,
  handleChange: HandleChangeFn
) => {
  const newState = produce(prevState, draft => {
    if (draft[field].subFields) {
      draft?.[field]?.subFields?.push(
        formatFormState(array, array, handleChange, draft?.[field]?.subFields?.length, field)
      );
    }
  });
  return newState;
};

export const setValuesInState = (
  state: Fields,
  values: Record<string, string | Record<string, string>[]>,
  handleChange: HandleChangeFn
) => {
  const newState = produce(state, draft => {
    Object.keys(values).forEach(key => {
      if (draft[key] && values[key] !== undefined) {
        if (!Array.isArray(values[key])) {
          if (typeof values[key] === 'string') {
            draft[key].value = values[key] as string;
            draft[key].dirty = true;
            draft[key].error = undefined;
            draft[key].touched = true;
          }
        }
        if (Array.isArray(values[key]) && typeof values[key] !== 'string') {
          const valuesArray = values[key] as Record<string, string>[];
          valuesArray.forEach((item: Record<string, string>, index: number) => {
            Object.keys(item).forEach(subKey => {
              if (draft[key]?.subFields && item[subKey] !== undefined) {
                const subFields = draft[key].subFields;
                if (subFields && !subFields[index]) {
                  const firstSubField = subFields[0];
                  if (firstSubField) {
                    subFields[index] = formatFormState(
                      firstSubField,
                      firstSubField,
                      handleChange,
                      index,
                      key,
                      true
                    );
                  }
                }
                if (subFields?.[index]?.[subKey]) {
                  subFields[index][subKey].value = item[subKey];
                  subFields[index][subKey].dirty = true;
                  subFields[index][subKey].error = undefined;
                  subFields[index][subKey].touched = true;
                }
              }
            });
          });
        }
      }
    });
  });
  return newState;
};

export const formatWithInitialValues = (state: Fields, initialValues?: Record<string, unknown>) => {
  if (!initialValues) {
    return state;
  }
  const newState = produce(state, draft => {
    Object.keys(initialValues).forEach(key => {
      if (draft[key]) {
        if (draft[key].type === 'sub' && Array.isArray(initialValues[key])) {
          const array = initialValues[key] as Record<string, unknown>[];
          const clonedDefaults = produce(draft[key].subFields?.[0], subFields => subFields);
          if (clonedDefaults) {
            draft[key].subFields = [];
            array.forEach((item: Record<string, unknown>) => {
              const obj = formatWithInitialValues(clonedDefaults, item);
              draft[key].subFields?.push(obj);
            });
          }
        } else {
          draft[key].value = formatValue(false, draft[key].value, initialValues[key]);
          if (typeof initialValues[key] === 'object' && initialValues[key] !== null) {
            draft[key].objectValue = initialValues[key];
          }
        }
      } else {
        draft[key] = {
          type: 'value',
          id: key,
          value: (() => formatValue(false, undefined, initialValues[key]))(),
          originalValue: initialValues[key],
          dirty: false,
          error: undefined,
          touched: false,
        };
      }
    });
  });
  return newState;
};

const getErrorObj = (errors: ValidationError) =>
  errors?.inner?.reduce((prev, curr) => {
    return { ...prev, [curr.path as string]: curr?.message };
  }, {});

export const useForm = ({ fields, validations, initialValues }: UseFormProps) => {
  const [formState, setFormState] = useState<Fields>(fields);
  const [isDirty, setIsDirty] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<Record<keyof typeof fields, string>>({});
  const previousFormValuesRef = useRef<string>('');

  const updateFieldError = useCallback((fieldPath: string, errorMessage: string | undefined) => {
    setFormState(prevState => {
      return produce(prevState, draft => {
        // parse the field path to extract fieldId, subIndex, and subKey
        const pathParts = fieldPath.split('.');
        if (![1, 3].includes(pathParts.length)) {
          return;
        }
        const fieldId: string = pathParts.length === 3 ? pathParts[2] : pathParts[0];
        const subIndex: number = pathParts.length === 3 ? Number(pathParts[1]) : -1;
        const subKey: string = pathParts.length === 3 ? pathParts[0] : '';
        let targetField: InputProps | undefined;
        if (subIndex > -1 && subKey) {
          targetField = draft[subKey]?.subFields?.[Number(subIndex)]?.[fieldId];
        } else {
          targetField = draft[fieldId];
        }
        if (targetField) {
          targetField.error = errorMessage;
        }
      });
    });

    // update the errors state
    setErrors(prev => {
      if (errorMessage === undefined) {
        const newErrors = { ...prev };
        delete newErrors[fieldPath as keyof typeof fields];
        return newErrors;
      }
      return {
        ...prev,
        [fieldPath]: errorMessage,
      };
    });
  }, []);

  // validate a single field
  const validateField = useCallback(
    async (fieldPath: string, fieldValue: unknown) => {
      try {
        const validationData = { [fieldPath]: fieldValue };
        await validations.validateAt(fieldPath, validationData);
        updateFieldError(fieldPath, undefined);
      } catch (err) {
        if (err instanceof ValidationError) {
          updateFieldError(fieldPath, err.message);
        }
      }
    },
    [validations, updateFieldError]
  );

  const handleChange = useCallback(
    (fieldId: string, subIndex: number = -1, subKey: string = '') =>
      (value: unknown, objectValue?: ObjectValue) => {
        setFormState(prevState => {
          return produce(prevState, draft => {
            let targetField: InputProps | undefined;

            if (subIndex > -1 && subKey) {
              targetField = draft[subKey]?.subFields?.[subIndex]?.[fieldId];
            } else {
              targetField = draft[fieldId];
            }
            if (!targetField) return;
            const prevTouched = targetField.touched;
            targetField.touched = true;
            const isSame =
              (targetField.value === trim(value) ||
                (targetField.value === undefined && trim(value) === '')) &&
              JSON.stringify(targetField.objectValue) === JSON.stringify(objectValue);

            if (isSame) {
              // field was touched for first time, but value didn't change - validate just this field
              if (!prevTouched) {
                const fieldPath =
                  subIndex > -1 && subKey ? `${subKey}.${subIndex}.${fieldId}` : fieldId;
                const fieldValue = targetField.value;
                validateField(fieldPath, fieldValue);
              }
              return;
            }

            const orig = original(targetField);
            if (JSON.stringify(orig?.value) !== JSON.stringify(value)) {
              setIsValidating(true);
            }
            setIsDirty(true);
            targetField.value = value as string;
            targetField.dirty = true;
            targetField.objectValue = objectValue;
            targetField.error = undefined;
          });
        });
      },
    []
  );

  useEffect(() => {
    setFormState(state =>
      formatFormState(formatWithInitialValues(state, initialValues), fields, handleChange)
    );
  }, [fields, initialValues, handleChange]);

  useEffect(() => {
    const validateForm = async () => {
      setIsValidating(true);
      try {
        const formattedState = formatSubmit(formState);
        await validations.validate(formattedState, { abortEarly: false });
        setFormState(prevState => {
          return produce(prevState, draft => {
            Object.keys(draft).forEach(key => {
              if (draft[key]) {
                draft[key].error = undefined;
                if (draft[key].subFields) {
                  draft[key].subFields?.forEach(subItem => {
                    Object.keys(subItem).forEach(subKey => {
                      subItem[subKey].error = undefined;
                    });
                  });
                }
              }
            });
          });
        });
        setErrors({});
      } catch (err) {
        if (err instanceof ValidationError) {
          setErrors(getErrorObj(err));
          if (err as ValidationError) {
            const { inner } = err;
            setFormState(prevState => setErrorsInFormState(prevState, inner));
          }
        }
      } finally {
        setIsValidating(false);
      }
    };

    // get current form values as a string to compare
    const currentFormValues = JSON.stringify(formatSubmit(formState));

    // only validate if the form values have actually changed
    if (currentFormValues !== previousFormValuesRef.current) {
      previousFormValuesRef.current = currentFormValues;

      const handler = setTimeout(() => {
        validateForm();
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [formState, validations]);

  const submit = async (apiCallFn?: (data: Submit) => Promise<void>) => {
    setIsValidating(true);

    const formattedState = formatSubmit(formState);
    let validationErrors: string[] = [];
    let validationinner: ValidationError[] = [];
    const data: Submit = formattedState;
    let apiError: unknown | undefined = undefined;

    try {
      // perform Yup validation
      await validations.validate(data, { abortEarly: false });
      // if Yup validation passes, clear all errors
      setFormState(prevState => {
        return produce(prevState, draft => {
          Object.keys(draft).forEach(key => {
            if (draft[key]) {
              draft[key].error = undefined;
              if (draft[key].subFields) {
                draft[key].subFields?.forEach(subItem => {
                  Object.keys(subItem).forEach(subKey => {
                    subItem[subKey].error = undefined;
                  });
                });
              }
            }
          });
        });
      });
      setErrors({});
      if (apiCallFn) {
        await apiCallFn(formattedState);
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        // yup validation failed
        validationErrors = err.errors;
        validationinner = err.inner;
        setErrors(getErrorObj(err));
        setFormState(prevState => setErrorsInFormState(prevState, validationinner));
      } else {
        // this is an error from the API call
        apiError = err;
        setErrors(prev => ({
          ...prev,
          apiError: `API Error: ${err instanceof Error ? err.message : String(err)}`,
        }));
      }
    } finally {
      // set false only after Yup validation AND API call are complete
      setIsValidating(false);
    }
    return { errors: validationErrors, data: formattedState, apiError }; // return apiError too
  };

  const addToArray = useCallback(
    (field: string, array: Fields) => {
      setFormState(prevState => addArrayToState(prevState, field, array, handleChange));
      setIsDirty(true);
    },
    [handleChange]
  );

  const removeFromArray = useCallback((field: string, indexToRemove: number) => {
    setFormState(prevState =>
      produce(prevState, draft => {
        if (draft[field]?.subFields) {
          setIsDirty(true);
          draft[field].subFields?.splice(indexToRemove, 1);
        }
      })
    );
  }, []);

  const setValues = useCallback(
    (values: Record<string, string | Record<string, string>[]>) => {
      setFormState(state => setValuesInState(state, values, handleChange));
      setIsDirty(true);
    },
    [handleChange]
  );

  const resetForm = useCallback(() => {
    setFormState(prevFields =>
      formatFormState(
        formatWithInitialValues(prevFields, initialValues),
        fields,
        handleChange,
        undefined,
        undefined,
        true
      )
    );
    setIsDirty(false);
    setErrors({});
  }, [fields, initialValues, handleChange]);

  const getField = useCallback(
    (fieldId: string, subIndex?: number, subKey?: string) => {
      if (subIndex !== undefined && subKey) {
        return formState[subKey]?.subFields?.[subIndex]?.[fieldId];
      }
      return formState[fieldId];
    },
    [formState]
  );

  const disableBtn = !isDirty || isValidating || Object.keys(errors ?? {})?.length > 0;

  return {
    fields: formState,
    submit,
    addToArray,
    removeFromArray,
    isDirty,
    setValues,
    errors,
    isErrors: Object.keys(errors ?? {})?.length > 0,
    isValidating,
    resetForm,
    getField,
    disableBtn,
  };
};

export const setErrorsInFormState = (state: Fields, errors: ValidationError['inner']): Fields => {
  return produce(state, draft => {
    // first, clear all existing errors
    const clearErrorsRecursively = (obj: unknown) => {
      if (obj && typeof obj === 'object') {
        if ('error' in obj) {
          (obj as { error?: string }).error = undefined;
        }
        if ('subFields' in obj && Array.isArray((obj as { subFields?: unknown[] }).subFields)) {
          (obj as { subFields?: unknown[] }).subFields?.forEach((subItem: unknown) => {
            clearErrorsRecursively(subItem);
          });
        }
        // also check for nested objects that might have error properties
        Object.keys(obj as Record<string, unknown>).forEach(key => {
          const value = (obj as Record<string, unknown>)[key];
          if (value && typeof value === 'object' && 'error' in value) {
            (value as { error?: string }).error = undefined;
          }
        });
      }
    };

    // clear all errors first
    Object.keys(draft).forEach(key => {
      clearErrorsRecursively(draft[key]);
    });

    // then set only the current errors
    errors.forEach(error => {
      if (!error.path) return;
      const pathParts = error.path?.replace(/\[(\d+)\]/g, '.$1').split('.');
      if (pathParts.length === 0) return;

      let current: Fields | Fields[] | undefined = draft;
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        const isArrayIndex = !isNaN(Number(part));

        if (isArrayIndex) {
          const index = Number(part);
          if (Array.isArray(current) && current[index]) {
            current = current[index];
          } else {
            current = undefined;
            break;
          }
        } else if (
          current &&
          !Array.isArray(current) &&
          Object.prototype.hasOwnProperty.call(current, part)
        ) {
          if (i === pathParts.length - 1) {
            if (current[part] && typeof current[part] === 'object' && 'error' in current[part]) {
              (current[part] as { error?: string }).error = error.message;
            }
          } else if (
            current[part] &&
            typeof current[part] === 'object' &&
            'subFields' in current[part]
          ) {
            current = (current[part] as { subFields?: Fields[] }).subFields;
          } else if (current[part]) {
            // handle cases where it's not subFields but a nested object structure
            current = current[part] as unknown as Fields;
          } else {
            current = undefined;
            break;
          }
        } else {
          current = undefined;
          break;
        }
      }
    });
  });
};
